import { useState } from 'react';
import { useMutation } from 'react-query';
import { X, MessageSquare, Hash, AlignLeft, FileText } from 'lucide-react';
import { createTemplate, updateTemplate, type Template, type CreateTemplateRequest } from '@/services/templatesService';
import toast from 'react-hot-toast';

interface Props {
  template: Template | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function TemplateFormModal({ template, onClose, onSuccess }: Props) {
  const [formData, setFormData] = useState<CreateTemplateRequest>({
    channel: template?.channel || 'whatsapp',
    name: template?.name || '',
    language: template?.language || 'pt_BR',
    status: template?.status || 'draft',
    content: {
      type: 'text',
      header: template?.content.header || '',
      body: template?.content.body || '',
      footer: template?.content.footer || '',
      variables: template?.content.variables || [],
    },
  });

  const mutation = useMutation({
    mutationFn: (data: CreateTemplateRequest) => 
      template ? updateTemplate(template.id, data) : createTemplate(data),
    onSuccess: () => {
      toast.success(template ? 'Template atualizado!' : 'Template criado!');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Erro ao salvar template');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <MessageSquare className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{template ? 'Editar Template' : 'Novo Template'}</h2>
              <p className="text-blue-100 text-sm">Preencha os campos abaixo para criar seu template</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="p-6 space-y-6">
            {/* Nome e Canal */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <Hash size={16} className="text-blue-600" />
                  Nome do Template *
                </label>
                <input 
                  required 
                  value={formData.name} 
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                  placeholder="Ex: boas_vindas" 
                />
                <p className="text-xs text-gray-500 mt-1">Use snake_case sem espaços</p>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <MessageSquare size={16} className="text-blue-600" />
                  Canal
                </label>
                <select 
                  value={formData.channel} 
                  onChange={(e) => setFormData({ ...formData, channel: e.target.value as any })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  disabled={!!template}
                >
                  <option value="whatsapp">WhatsApp</option>
                  <option value="webchat">WebChat</option>
                </select>
              </div>
            </div>
            {/* Info WhatsApp */}
            {formData.channel === 'whatsapp' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <div className="text-amber-600 mt-0.5">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900">Template WhatsApp</p>
                    <p className="text-xs text-amber-700 mt-1">
                      Templates WhatsApp precisam ser aprovados pela Meta antes de serem usados. O status inicial será "Pendente".
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* Header */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <FileText size={16} className="text-blue-600" />
                Cabeçalho (opcional)
              </label>
              <input 
                value={formData.content.header || ''} 
                onChange={(e) => setFormData({ ...formData, content: { ...formData.content, header: e.target.value }})}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                placeholder="Ex: 👋 Olá {{nome}}!" 
              />
            </div>
            {/* Body */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <AlignLeft size={16} className="text-blue-600" />
                Corpo da Mensagem *
              </label>
              <textarea 
                required 
                rows={6} 
                value={formData.content.body || ''} 
                onChange={(e) => setFormData({ ...formData, content: { ...formData.content, body: e.target.value }})}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono text-sm" 
                placeholder="Digite a mensagem do template...&#10;&#10;Use {{variavel}} para inserir dados dinâmicos." 
              />
              <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-1">💡 Variáveis disponíveis:</p>
                <div className="flex flex-wrap gap-2">
                  <code className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">{`{{nome}}`}</code>
                  <code className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">{`{{data}}`}</code>
                  <code className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">{`{{valor}}`}</code>
                  <code className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">{`{{produto}}`}</code>
                </div>
              </div>
            </div>
            {/* Footer */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <FileText size={16} className="text-blue-600" />
                Rodapé (opcional)
              </label>
              <input 
                value={formData.content.footer || ''} 
                onChange={(e) => setFormData({ ...formData, content: { ...formData.content, footer: e.target.value }})}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                placeholder="Ex: Equipe de Atendimento" 
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 bg-gray-50 px-6 py-4 -mx-6 -mb-6 mt-6">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              disabled={mutation.isLoading} 
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all shadow-lg shadow-blue-500/30 flex items-center gap-2"
            >
              {mutation.isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Salvando...
                </>
              ) : (
                <>
                  <MessageSquare size={16} />
                  {template ? 'Atualizar Template' : 'Criar Template'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
